import { Suspense } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { graphql, useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import { TrashSimple } from "@phosphor-icons/react"
import { useDirtyDrawerMaker } from "@/components/drawer-wrapper"
import { Button } from "@/components/ui/button"
import { DetailCheckbox } from "@/components/ui/checkbox"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { TextArea } from "@/components/ui/input"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { withErrorBoundary } from "@/hoc/with-error-boundary"
import { useToast } from "@/hooks/useToast"
import { textAreaDefaultMaxLength } from "@/models/common"
import { chLaboratoryPrescriptionDrawerFragment$key } from "@/modules/patients/detail/laboratory-prescription/ch/__generated__/chLaboratoryPrescriptionDrawerFragment.graphql"
import { LaboratoryTestSelection } from "@/modules/patients/detail/laboratory-prescription/ch/components/laboratory-test-selection"
import { DOCUMENT_LANGUAGES } from "@/modules/patients/detail/laboratory-prescription/ch/constants"
import {
  LaboratoryPrescription,
  LaboratoryPrescriptionSchema,
} from "@/modules/patients/detail/laboratory-prescription/ch/schema/laboratory-prescription-schema"
import { useChCreateLaboratoryPrescriptionMutation } from "@/modules/patients/detail/laboratory-prescription/ch/services/useChCreateLaboratoryPrescriptionMutation"
import { useChDeleteLaboratoryPrescriptionMutation } from "@/modules/patients/detail/laboratory-prescription/ch/services/useChDeleteLaboratoryPrescriptionMutation"
import { useChGenerateLaboratoryPrescriptionMutation } from "@/modules/patients/detail/laboratory-prescription/ch/services/useChGenerateLaboratoryPrescriptionMutation"
import { useChUpdateLaboratoryPrescriptionMutation } from "@/modules/patients/detail/laboratory-prescription/ch/services/useChUpdateLaboratoryPrescriptionMutation"
import { LastModificationInfo } from "@/modules/patients/detail/last-modification-info"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { getDocumentLanguageLabel } from "@/modules/patients/hooks/useDefaultDocumentLanguage"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { chProgressNoteFragment$data } from "@/modules/patients/services/ch/__generated__/chProgressNoteFragment.graphql"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { Language } from "@/types/__generated__/union-types"

const chLaboratoryPrescriptionFragment = graphql`
  fragment chLaboratoryPrescriptionDrawerFragment on ch_LaboratoryPrescription {
    id
    patientId
    progressNoteId
    createdAt
    createdBy
    signedAt
    signedBy
    language
    externalExaminations
    pointOfCareExaminations
    isDoc2u
    additionalRemark
    updatedAt
    updatedBy
    sentAt
    sentBy
    concludedAt
    concludedBy
    fileId
    fileMetadata {
      documentType
      extension
      lengthInKilobytes
    }
  }
`

interface ChLaboratoryPrescriptionDrawerProps {
  progressNoteId: string
  id?: string
  laboratoryPrescriptionRef?: chLaboratoryPrescriptionDrawerFragment$key
  medgateConnectOrder?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["medgateConnectOrder"]
}

const ChLaboratoryPrescriptionDrawer = withErrorBoundary(
  ({ progressNoteId, id, laboratoryPrescriptionRef, medgateConnectOrder }: ChLaboratoryPrescriptionDrawerProps) => {
    const { t } = useTranslation()
    const { toast } = useToast()
    const closeDrawer = useDrawerStore((state) => state.closeDrawer)
    const [createLaboratoryPrescription, isCreating] = useChCreateLaboratoryPrescriptionMutation()
    const [updateLaboratoryPrescription, isUpdating] = useChUpdateLaboratoryPrescriptionMutation()
    const [deleteLaboratoryPrescription, isDeleting] = useChDeleteLaboratoryPrescriptionMutation()
    const [generateLaboratoryPrescription, isGenerating] = useChGenerateLaboratoryPrescriptionMutation()
    const contextAppointment = useContextAppointment()
    const appointmentLanguage = contextAppointment?.language
    const patientId = usePatientParam()
    const isEditMode = !!id
    const isAddMode = !isEditMode && patientId && progressNoteId
    const persistedData = useFragment(chLaboratoryPrescriptionFragment, laboratoryPrescriptionRef)
    const updatedAt = persistedData?.updatedAt ?? persistedData?.createdAt
    const updatedBy = persistedData?.updatedBy ?? persistedData?.createdBy

    const confirm = useConfirmStore((state) => state.confirm)
    const cancel = useConfirmStore((state) => state.cancel)

    const parsedData = isEditMode && persistedData ? LaboratoryPrescriptionSchema.parse(persistedData) : null
    const defaultLanguage: Language =
      DOCUMENT_LANGUAGES.find((lang) => lang.value === appointmentLanguage)?.value ?? "GERMAN"
    const form = useForm<LaboratoryPrescription>({
      mode: "all",
      resolver: zodResolver(LaboratoryPrescriptionSchema),
      defaultValues: parsedData ?? {
        language: defaultLanguage,
        pointOfCareExaminations: [],
        externalExaminations: [],
        isDoc2u: false,
      },
    })

    const {
      control,
      formState: { isDirty, errors },
      register,
      watch,
      trigger,
    } = form

    useDirtyDrawerMaker(isDirty)

    const watchPointOfCareExaminations = watch("pointOfCareExaminations")
    const watchExternalExaminations = watch("externalExaminations")

    const handleCloseDrawer = () => {
      closeDrawer()
      FaroInsightsWrapper.pushEvent("documents_laboratory_prescription_close")
    }

    const onSubmit: SubmitHandler<LaboratoryPrescription> = (data) => {
      if (!progressNoteId) return

      const { language, pointOfCareExaminations, externalExaminations, isDoc2u, additionalRemark } = data

      const payload = {
        language,
        pointOfCareExaminations,
        externalExaminations,
        isDoc2u,
        additionalRemark,
      }

      if (isAddMode) {
        createLaboratoryPrescription({
          variables: {
            input: {
              language: payload.language,
              pointOfCareExaminations: payload.pointOfCareExaminations,
              externalExaminations: payload.externalExaminations,
              isDoc2u: payload.isDoc2u,
              additionalRemark: payload.additionalRemark,
              patientId,
              progressNoteId,
            },
          },
          onCompleted(response) {
            const id = response.ch_createLaboratoryPrescription.ch_LaboratoryPrescription?.id
            if (id) {
              handleGenerateLaboratoryPrescription(id)
            }
          },
          updater(store, data) {
            try {
              const progressNoteRecordProxy = store.get(progressNoteId)
              const laboratoryPrescription = data?.ch_createLaboratoryPrescription.ch_LaboratoryPrescription
              if (laboratoryPrescription?.id) {
                const newRecordProxy = store.get(laboratoryPrescription.id)
                if (newRecordProxy) {
                  progressNoteRecordProxy?.setLinkedRecord(newRecordProxy, "ch_laboratoryPrescription")
                }
              }
            } catch (error) {
              console.error("Error while processing data:", error)
            }
          },
        })
      }

      if (isEditMode) {
        updateLaboratoryPrescription({
          variables: {
            input: {
              id,
              language: payload.language,
              pointOfCareExaminations: payload.pointOfCareExaminations,
              externalExaminations: payload.externalExaminations,
              isDoc2u: payload.isDoc2u,
              additionalRemark: payload.additionalRemark,
            },
          },
          onCompleted({ ch_updateLaboratoryPrescription }) {
            const id = ch_updateLaboratoryPrescription.ch_LaboratoryPrescription?.id
            if (id) {
              handleGenerateLaboratoryPrescription(id)
            }
          },
        })
      }
    }

    const onDelete = () => {
      if (progressNoteId && id) {
        deleteLaboratoryPrescription({
          variables: {
            input: {
              id,
            },
          },
          onCompleted() {
            handleCloseDrawer()
            toast({
              description: t("patient.laboratoryPrescription.deleteMessage.success"),
            })
            cancel()
          },
        })
      }
    }

    const handleDelete = () => {
      confirm({
        title: t("patient.laboratoryPrescription.deleteConfirmTitle"),
        content: t("episodeOfCare.deleteProgressNote.content"),
        onConfirm: onDelete,
        confirmVariant: "danger",
        confirmText: t("common.button.delete"),
        cancelText: t("common.button.keep"),
        isLoading: isDeleting,
      })
    }

    const handleGenerateLaboratoryPrescription = (id: string) => {
      generateLaboratoryPrescription({
        id,
        medgateConnectOrderCode: medgateConnectOrder?.orderCode,
        onCompleted() {
          handleCloseDrawer()
        },
      })
    }

    return (
      <Drawer open onOpenChange={handleCloseDrawer}>
        <FormProvider {...form}>
          <DrawerContent>
            <DrawerClose className="top-2 right-2" />
            <DrawerHeader className="pb-4" data-testid="prescription-for-laboratory-header">
              <DrawerTitle className="animate-fade-in-bottom text-on-surface-subdued text-sm font-medium">
                {t("patient.laboratoryPrescription.prescriptionForMedgateConnect")}
              </DrawerTitle>
            </DrawerHeader>
            <DrawerBody data-testid="prescription-for-laboratory-body">
              <div className="mb-8 flex flex-col gap-4">
                <FormField
                  control={control}
                  name="language"
                  render={({
                    field: { name, onChange, value, ref },
                    formState: { errors },
                    fieldState: { invalid },
                  }) => (
                    <div>
                      <Select value={value} onValueChange={onChange} {...ref}>
                        <SelectTrigger hasError={invalid} className="w-56.5">
                          <SelectValue asChild>
                            <LabelAndSelectValue
                              data-testid="document-language"
                              label={t("patient.laboratoryPrescription.documentLanguage") + "*"}
                              value={t(getDocumentLanguageLabel(value, true))}
                            />
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_LANGUAGES.map((language) => (
                            <SelectItem
                              value={language.value}
                              key={language.value}
                              data-testid={`document-language-${language.value.toLocaleLowerCase()}`}>
                              {t(language.label)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <TranslatedFieldError errors={errors} name={name} />
                    </div>
                  )}
                />
              </div>
              <div className="flex flex-col gap-4">
                <label className="text-on-surface text-base font-medium">
                  {t("patient.laboratoryPrescription.for")}
                </label>
                <Suspense>
                  <LaboratoryTestSelection />
                </Suspense>
                {(watchPointOfCareExaminations.length > 0 || watchExternalExaminations.length > 0) && (
                  <TextArea
                    {...register("additionalRemark")}
                    label={t("patient.laboratoryPrescription.additionalRemark")}
                    data-testid="additional-remark-field"
                    textLimit={textAreaDefaultMaxLength}
                    error={errors?.additionalRemark?.message}
                    translateError
                  />
                )}
                <FormField
                  name="isDoc2u"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <DetailCheckbox
                      checked={value}
                      onCheckedChange={(isChecked) => {
                        onChange(isChecked)
                        if (isChecked) trigger("pointOfCareExaminations")
                      }}
                      data-testid="doc2u-checkbox">
                      {t("patient.laboratoryPrescription.doc2u")}
                    </DetailCheckbox>
                  )}
                />
              </div>
            </DrawerBody>
            <DrawerFooter className="flex-wrap gap-2" data-testid="prescription-for-laboratory-footer">
              {isEditMode && (
                <div className="flex gap-2">
                  {updatedAt && updatedBy && <LastModificationInfo updatedAt={updatedAt} updatedBy={updatedBy} />}
                  <button
                    onClick={() => {
                      FaroInsightsWrapper.pushEvent("documents_laboratory_prescription_delete")
                      handleDelete()
                    }}
                    data-testid="prescription-for-laboratory-delete-button">
                    <TrashSimple className="text-on-surface-subdued" />
                  </button>
                </div>
              )}
              <div className="ml-auto flex gap-2">
                <Button
                  data-testid="prescription-for-laboratory-cancel-button"
                  variant="secondary"
                  onClick={handleCloseDrawer}>
                  {t("common.button.cancel")}
                </Button>
                <Button
                  disabled={isEditMode && !isDirty}
                  data-testid="prescription-for-laboratory-save-button"
                  isLoading={isCreating || isUpdating || isGenerating}
                  onClick={() => {
                    FaroInsightsWrapper.pushEvent("documents_laboratory_prescription_save")
                    form.handleSubmit(onSubmit)()
                  }}>
                  {t("common.button.save")}
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </FormProvider>
      </Drawer>
    )
  },
  "ChLaboratoryPrescriptionDrawer",
)

export { ChLaboratoryPrescriptionDrawer }
